import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

type TooltipRootProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>

const TooltipStateContext = React.createContext<{ open: boolean; setOpen: (o: boolean) => void } | null>(null)

const Tooltip: React.FC<TooltipRootProps> = ({ children, ...props }) => {
  const [open, setOpen] = React.useState(false)
  return (
    <TooltipStateContext.Provider value={{ open, setOpen }}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} delayDuration={0} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipStateContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onClick, onPointerDown, onKeyDown, tabIndex, asChild = true, ...props }, ref) => {
  const ctx = React.useContext(TooltipStateContext)
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      onPointerDown={(e) => {
        onPointerDown?.(e)
        ;(e.currentTarget as HTMLElement).focus()
      }}
      onClick={(e) => {
        onClick?.(e)
        ;(e.currentTarget as HTMLElement).focus()
        if (ctx) ctx.setOpen(!ctx.open)
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e)
        if ((e.key === "Enter" || e.key === " ") && ctx) {
          e.preventDefault()
          ctx.setOpen(!ctx.open)
        }
      }}
      tabIndex={tabIndex ?? 0}
      {...props}
    />
  )
})
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align="center"
      avoidCollisions
      collisionPadding={12}
      className={cn(
        "z-50 overflow-hidden rounded-md border border-[#243a66] bg-[#2D4C7F] px-3 py-1.5 text-sm text-white shadow-xl ring-1 ring-black/10 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
